import { useMemo } from 'react';

import {
  Box,
  Icons,
  Spinner,
  Stack,
  Typography,
  tokens,
} from '@cast/design-system';

import {
  CheckStepStatus,
  CONNECTING_CHECK_STEPS,
  CONNECTING_PHASE_COPY,
} from '../../_constants';

type ConnectingChecklistProps = {
  phase: 'connecting' | 'ready';
  leadText: string;
  activeChecklistStep: number;
};

const getStepStatus = (index: number, activeStep: number): CheckStepStatus => {
  if (index < activeStep) {
    return 'done';
  }
  if (index === activeStep) {
    return 'active';
  }
  return 'pending';
};

const ChecklistIcon = ({ status }: { status: CheckStepStatus }) => {
  if (status === 'done') {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          backgroundColor: tokens.theme.bg.selection.primary,
          flexShrink: 0,
        }}
      >
        <Icons.Check
          size={16}
          color={tokens.theme.fg.structural.primaryOnInverse}
        />
      </Stack>
    );
  }

  if (status === 'active') {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{ width: 28, height: 28, flexShrink: 0 }}
      >
        <Spinner color="primary" size={16} />
      </Stack>
    );
  }

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ width: 28, height: 28, flexShrink: 0 }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: tokens.theme.bg.structural.surfaceSubtle,
        }}
      />
    </Stack>
  );
};

export const ConnectingChecklist = ({
  phase,
  leadText,
  activeChecklistStep,
}: ConnectingChecklistProps) => {
  const introText = useMemo(() => {
    if (phase === 'ready') {
      return leadText;
    }

    return CONNECTING_PHASE_COPY.intro;
  }, [leadText, phase]);

  const displayStep =
    phase === 'ready' ? CONNECTING_CHECK_STEPS.length : activeChecklistStep;

  return (
    <Stack gap={24} width={604} pr={20}>
      <Typography variant="P14R" color={tokens.theme.fg.structural.secondary}>
        {introText}
      </Typography>

      <Stack gap={16}>
        {CONNECTING_CHECK_STEPS.map((step, index) => {
          const status = getStepStatus(index, displayStep);

          return (
            <Stack
              key={step.title}
              direction="row"
              gap={12}
              alignItems="flex-start"
            >
              <ChecklistIcon status={status} />

              <Stack gap={2}>
                <Typography
                  variant="P14M"
                  color={tokens.theme.fg.structural.primary}
                >
                  {step.title}
                </Typography>
                <Typography
                  variant="P12R"
                  color={tokens.theme.fg.structural.secondary}
                >
                  {step.description}
                </Typography>
              </Stack>
            </Stack>
          );
        })}
      </Stack>

      {phase === 'connecting' ? (
        <Typography variant="P12R" color={tokens.theme.fg.structural.secondary}>
          {CONNECTING_PHASE_COPY.backgroundNote}
        </Typography>
      ) : null}
    </Stack>
  );
};
